/**
 * Epic 2 — ruční synchronizace s DB. Sebeobsažené tlačítko + modál (stejný
 * vzor jako `ViewSettingsPanel.tsx`) v top-right panelu canvasu vedle
 * "Auto-layout" (Epic 7) — sync i auto-layout jsou obě "rozsáhlé, ale
 * kontrolované" akce nad celým modelem.
 *
 * Merge (E2-US4) se PŘEHRÁVÁ přes existující Epic 4/6/7 command vrstvu
 * (`schema/syncMerge.ts#buildSyncMergePlan`, canvas úklid/umístění v
 * `syncApply.ts#buildApplyBundle` — vytaženo odsud v Epic 9, aby ho mohl
 * beze změny sdílet i `ChangeScriptPanel.tsx`) — žádná paralelní "aplikuj
 * diff" logika. Podle `doc/epic-13-undo-redo.md`
 * kap. 2 je sync/merge NEVRATNÁ operace (undo zásobník se po dokončení
 * vyprázdní, `useCommandStack().clear()`) — přesto se commandy pouští přes
 * normální `execute()`, aby UI zůstalo reaktivní stejně jako u každé jiné
 * editace; zásobník se vyprázdní hned po úspěchu, takže krátkodobé
 * zaznamenání do undo historie nemá žádný pozorovatelný důsledek.
 *
 * Perzistence NEJDE přes debounced autosave (`schema/store.tsx`/
 * `canvas/store.tsx`) — ten by baseline zápis do historie mohl předběhnout
 * (E2-US5 vyžaduje, aby nová `dbSync` baseline existovala HNED, ne až za
 * 800ms). Místo toho se stejné `mergedSchema`/`mergedCanvas`, které se pošlou
 * do `execute()`, pošlou i přímo do `POST /sync/baseline` (`api.applySyncBaseline`)
 * — jedna uzamčená backendová operace uloží obojí a zapíše historii atomicky.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { CheckSquare, GitMerge, RefreshCw, X } from 'lucide-react';
import type { CanvasMetadata, SchemaModel, SyncDiffItem, SyncScanJobStatus } from '@bits2schema/shared';
import { diffItemKey } from '@bits2schema/shared';
import { api } from '../api/client';
import { useCommandStack } from '../command/stack';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useT } from '../i18n';
import type { MessageKey } from '../i18n/types';
import { DIFF_CHANGE_TYPE_KEYS, DIFF_OBJECT_TYPE_KEYS } from '../utils/diffLabels';
import { describeError } from '../utils/format';
import type { Command as CanvasCommand } from './commands';
import type { BoxSize } from './geometry';
import type { Point } from './layout';
import { buildApplyBundle, collectRemovalWarnings, type ApplyBundle } from './syncApply';
import type { SchemaCommand } from '../schema/commands';

const SYNC_PHASE_KEYS: Record<SyncScanJobStatus['phase'], MessageKey> = {
  connecting: 'import.phase.connecting',
  readingTables: 'import.phase.readingTables',
  readingColumns: 'import.phase.readingColumns',
  readingConstraints: 'import.phase.readingConstraints',
  readingIndexes: 'import.phase.readingIndexes',
  buildingModel: 'import.phase.buildingModel',
  savingProject: 'import.phase.savingProject',
  computingDiff: 'sync.phase.computingDiff',
  done: 'import.phase.done',
};

const POLL_INTERVAL_MS = 500;

type Phase =
  | { kind: 'idle' }
  | { kind: 'scanning'; job: SyncScanJobStatus }
  | { kind: 'reviewing'; diff: SyncDiffItem[]; rescannedSchema: SchemaModel }
  | { kind: 'confirmingRemoval'; diff: SyncDiffItem[]; rescannedSchema: SchemaModel; bundle: ApplyBundle }
  | { kind: 'applying' }
  | { kind: 'done'; mergedCount: number }
  | { kind: 'error'; message: string };

interface SyncPanelProps {
  projectId: string;
  schema: SchemaModel;
  canvas: CanvasMetadata;
  schemaExecute: (command: SchemaCommand) => void;
  canvasExecute: (command: CanvasCommand) => void;
  effectiveTablePositions: Record<string, Point>;
  tableSizes: Record<string, BoxSize>;
}

/** Epic 9, E9-US7 — imperativní otevření/spuštění scanu zvenčí ("Skript jsem spustil → synchronizovat" v `ChangeScriptPanel.tsx`). */
export interface SyncPanelHandle {
  triggerScan: () => void;
}

export const SyncPanel = forwardRef<SyncPanelHandle, SyncPanelProps>(function SyncPanel(
  { projectId, schema, canvas, schemaExecute, canvasExecute, effectiveTablePositions, tableSizes }: SyncPanelProps,
  ref,
) {
  const t = useT();
  const { clear } = useCommandStack();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const applyingAutoRef = useRef(false);

  const closePanel = useCallback(() => {
    setOpen(false);
    setPhase({ kind: 'idle' });
  }, []);

  const startScan = useCallback(async () => {
    setOpen(true);
    setPhase({
      kind: 'scanning',
      job: {
        jobId: '',
        state: 'running',
        phase: 'connecting',
        tablesDone: 0,
        tablesTotal: null,
        startedAt: new Date().toISOString(),
        finishedAt: null,
      },
    });
    try {
      const { jobId } = await api.startSyncScan(projectId);
      setPhase((prev) => (prev.kind === 'scanning' ? { kind: 'scanning', job: { ...prev.job, jobId } } : prev));
    } catch (caught) {
      setPhase({ kind: 'error', message: describeError(caught, t) });
    }
  }, [projectId, t]);

  useImperativeHandle(ref, () => ({ triggerScan: () => void startScan() }), [startScan]);

  // Průběh re-scanu se dotazuje periodicky, dokud úloha běží (E2-US1 AC — "viditelný indikátor").
  useEffect(() => {
    if (phase.kind !== 'scanning' || !phase.job.jobId) return undefined;
    const jobId = phase.job.jobId;
    const timer = window.setInterval(async () => {
      try {
        const job = await api.getSyncScanJob(projectId, jobId);
        if (job.state === 'running') {
          setPhase({ kind: 'scanning', job });
        } else if (job.state === 'succeeded' && job.diff && job.rescannedSchema) {
          window.clearInterval(timer);
          setPhase({ kind: 'reviewing', diff: job.diff, rescannedSchema: job.rescannedSchema });
        } else if (job.state === 'failed' || job.state === 'succeeded') {
          window.clearInterval(timer);
          setPhase({ kind: 'error', message: job.error?.message ?? t('common.unknownError') });
        }
      } catch (caught) {
        window.clearInterval(timer);
        setPhase({ kind: 'error', message: describeError(caught, t) });
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [phase, projectId, t]);

  const applyBundle = useCallback(
    async (bundle: ApplyBundle) => {
      setPhase({ kind: 'applying' });
      try {
        for (const command of bundle.plan.commands) schemaExecute(command);
        for (const command of bundle.canvasCommands) canvasExecute(command);
        await api.applySyncBaseline(projectId, bundle.plan.mergedSchema, bundle.mergedCanvas, t('history.checkpoint.manualSync'));
        // E13-US5 — sync/merge je nevratná operace, undo zásobník se po dokončení vyprázdní.
        clear();
        setPhase({ kind: 'done', mergedCount: bundle.plan.commands.length });
      } catch (caught) {
        setPhase({ kind: 'error', message: describeError(caught, t) });
      }
    },
    [projectId, schemaExecute, canvasExecute, clear, t],
  );

  // E2-US2/US5 — nulový diff se slučuje bez jakéhokoliv potvrzení, ale baseline
  // se přesto zapíše (jinak by po ručním spuštění change scriptu app zůstala
  // "zaseklá" na starém baseline navždy).
  useEffect(() => {
    if (phase.kind !== 'reviewing' || phase.diff.length !== 0 || applyingAutoRef.current) return;
    applyingAutoRef.current = true;
    const bundle = buildApplyBundle(schema, canvas, phase.rescannedSchema, phase.diff, effectiveTablePositions, tableSizes);
    void applyBundle(bundle).finally(() => {
      applyingAutoRef.current = false;
    });
  }, [phase, schema, canvas, effectiveTablePositions, tableSizes, applyBundle]);

  const toggleItem = useCallback((key: string) => {
    setPhase((prev) => {
      if (prev.kind !== 'reviewing') return prev;
      return {
        ...prev,
        diff: prev.diff.map((item) => (diffItemKey(item) === key ? { ...item, selected: !item.selected } : item)),
      };
    });
  }, []);

  const selectAllAddedChanged = useCallback(() => {
    setPhase((prev) => {
      if (prev.kind !== 'reviewing') return prev;
      return { ...prev, diff: prev.diff.map((item) => (item.changeType === 'removed' ? item : { ...item, selected: true })) };
    });
  }, []);

  const startMerge = useCallback(() => {
    if (phase.kind !== 'reviewing') return;
    const bundle = buildApplyBundle(schema, canvas, phase.rescannedSchema, phase.diff, effectiveTablePositions, tableSizes);
    const hasRemovals = phase.diff.some((item) => item.selected && item.changeType === 'removed');
    if (hasRemovals) {
      setPhase({ kind: 'confirmingRemoval', diff: phase.diff, rescannedSchema: phase.rescannedSchema, bundle });
    } else {
      void applyBundle(bundle);
    }
  }, [phase, schema, canvas, effectiveTablePositions, tableSizes, applyBundle]);

  return (
    <>
      <button type="button" onClick={() => void startScan()}>
        <RefreshCw size={16} />
        {t('sync.button')}
      </button>

      {open &&
        (() => {
          // `confirmingRemoval` zobrazuje stejný diff jako `reviewing` (jen
          // needitovatelný, dokud běží vnořený `ConfirmDialog` níže) — sdílený
          // zdroj dat, aby se tabulka nekreslila na dvou místech dvakrát jinak.
          const diffToShow = phase.kind === 'reviewing' || phase.kind === 'confirmingRemoval' ? phase.diff : null;
          const diffReadOnly = phase.kind === 'confirmingRemoval';

          return (
            <Modal
              title={t('sync.title')}
              onClose={phase.kind === 'applying' || diffReadOnly ? () => {} : closePanel}
              wide
              actions={
                phase.kind === 'reviewing' ? (
                  <>
                    <button type="button" onClick={closePanel}>
                      <X size={16} />
                      {t('common.cancel')}
                    </button>
                    <button type="button" onClick={selectAllAddedChanged}>
                      <CheckSquare size={16} />
                      {t('sync.selectAllAddedChanged')}
                    </button>
                    <button type="button" className="primary" onClick={startMerge}>
                      <GitMerge size={16} />
                      {t('sync.merge.action')}
                    </button>
                  </>
                ) : phase.kind === 'done' || phase.kind === 'error' ? (
                  <button type="button" className="primary" onClick={closePanel}>
                    <X size={16} />
                    {t('common.close')}
                  </button>
                ) : (
                  <button type="button" disabled={phase.kind === 'applying' || diffReadOnly} onClick={closePanel}>
                    <X size={16} />
                    {t('common.cancel')}
                  </button>
                )
              }
            >
              <div className="stack sync-panel">
                {phase.kind === 'scanning' && (
                  <div className="stack">
                    <h3>{t(SYNC_PHASE_KEYS[phase.job.phase])}</h3>
                    <div className="progress">
                      <div
                        className="progress__bar"
                        style={{
                          width: `${
                            phase.job.tablesTotal && phase.job.tablesTotal > 0
                              ? Math.min(100, Math.round((phase.job.tablesDone / phase.job.tablesTotal) * 100))
                              : 15
                          }%`,
                        }}
                      />
                    </div>
                    <p className="hint">
                      {phase.job.tablesTotal === null
                        ? t('import.progressUnknownTotal', { done: phase.job.tablesDone })
                        : t('import.progress', { done: phase.job.tablesDone, total: phase.job.tablesTotal })}
                    </p>
                  </div>
                )}

                {diffToShow && diffToShow.length === 0 && <div className="notice">{t('sync.noDifferences')}</div>}

                {diffToShow && diffToShow.length > 0 && (
                  <table>
                    <thead>
                      <tr>
                        <th />
                        <th>{t('history.diff.column.object')}</th>
                        <th>{t('history.diff.column.path')}</th>
                        <th>{t('history.diff.column.old')}</th>
                        <th>{t('history.diff.column.new')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diffToShow.map((item) => {
                        const key = diffItemKey(item);
                        return (
                          <tr key={key} className={`diff-row diff-row--${item.changeType}`}>
                            <td>
                              <input
                                type="checkbox"
                                checked={item.selected}
                                disabled={diffReadOnly}
                                onChange={() => toggleItem(key)}
                              />
                            </td>
                            <td>
                              <span className="badge">{t(DIFF_OBJECT_TYPE_KEYS[item.objectType])}</span>
                              <div className="hint">{t(DIFF_CHANGE_TYPE_KEYS[item.changeType])}</div>
                            </td>
                            <td>{item.path}</td>
                            <td>{item.oldValue ?? '—'}</td>
                            <td>{item.newValue ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {phase.kind === 'applying' && <p className="hint">{t('sync.applying')}</p>}

                {phase.kind === 'done' && (
                  <div className="notice notice--success">
                    {phase.mergedCount === 0 ? t('sync.done.baselineOnly') : t('sync.done.merged', { count: phase.mergedCount })}
                  </div>
                )}

                {phase.kind === 'error' && <div className="notice notice--error">{phase.message}</div>}
              </div>
            </Modal>
          );
        })()}

      {phase.kind === 'confirmingRemoval' &&
        (() => {
          const removalCount = phase.diff.filter((item) => item.selected && item.changeType === 'removed').length;
          const warnings = collectRemovalWarnings(canvas, phase.bundle.plan.removedTables);
          return (
            <ConfirmDialog
              title={t('sync.confirmRemoval.title')}
              confirmLabel={t('sync.merge.action')}
              danger
              onCancel={() =>
                setPhase({ kind: 'reviewing', diff: phase.diff, rescannedSchema: phase.rescannedSchema })
              }
              onConfirm={() => void applyBundle(phase.bundle)}
              message={
                <>
                  <p>{t('sync.confirmRemoval.question', { count: removalCount })}</p>
                  {warnings.length > 0 && (
                    <ul>
                      {warnings.map((warning) => (
                        <li key={warning.table.id}>
                          {warning.groupName &&
                            t('sync.confirmRemoval.tableInGroup', { name: warning.table.name, group: warning.groupName })}
                          {warning.groupName && warning.noteCount > 0 && ' '}
                          {warning.noteCount > 0 &&
                            t('sync.confirmRemoval.tableHasNotes', { name: warning.table.name, count: warning.noteCount })}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="hint">{t('sync.confirmRemoval.hint')}</p>
                </>
              }
            />
          );
        })()}
    </>
  );
});
